import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@jq4race/i18n';
import { ResetPasswordRequestSchema } from '@jq4race/shared';
import { Button } from '@jq4race/ui';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { useAuthServices } from '../auth-services-context';
import { AuthShell } from '../components/AuthShell';
import { ErrorBanner } from '../components/ErrorBanner';
import { PasswordField } from '../components/PasswordField';

function buildResetSchema(t: (key: string) => string) {
  return ResetPasswordRequestSchema.omit({ resetToken: true })
    .extend({ confirm: z.string() })
    .refine((d) => d.newPassword === d.confirm, {
      message: t('shared.passwordMismatch'),
      path: ['confirm'],
    });
}

type ResetFormValues = z.infer<ReturnType<typeof buildResetSchema>>;

export function ResetPasswordPage() {
  const { t } = useTranslation('auth');
  const auth = useAuthServices();
  const { token } = useParams({ from: '/reset-password/$token' });
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  const resetSchema = useMemo(() => buildResetSchema(t), [t]);
  const form = useForm<ResetFormValues>({
    resolver: zodResolver(resetSchema),
    defaultValues: { newPassword: '', confirm: '' },
    mode: 'onTouched',
  });

  async function onSubmit(values: ResetFormValues) {
    setError(null);
    try {
      await auth.resetPassword({ resetToken: token, newPassword: values.newPassword });
      setDone(true);
    } catch (err) {
      setError(err);
    }
  }

  if (done) {
    return (
      <AuthShell
        title={t('resetPassword.done.title')}
        eyebrow={t('resetPassword.eyebrow')}
        description={t('resetPassword.done.description')}
        aside={{
          illustration: 'letter',
          eyebrow: t('resetPassword.aside.eyebrow'),
          headline: t('resetPassword.aside.headline'),
          body: t('resetPassword.done.aside.body'),
        }}
      >
        <Button className="w-full" onClick={() => navigate({ to: '/login' })}>
          {t('resetPassword.done.signIn')}
        </Button>
      </AuthShell>
    );
  }

  const { errors, isSubmitting } = form.formState;

  return (
    <AuthShell
      title={t('resetPassword.title')}
      eyebrow={t('resetPassword.eyebrow')}
      description={t('resetPassword.description')}
      aside={{
        illustration: 'letter',
        eyebrow: t('resetPassword.aside.eyebrow'),
        headline: t('resetPassword.aside.headline'),
        body: t('resetPassword.aside.body'),
      }}
    >
      <form className="grid gap-4" onSubmit={form.handleSubmit(onSubmit)} noValidate>
        <ErrorBanner error={error} />
        <Controller
          control={form.control}
          name="newPassword"
          render={({ field }) => (
            <PasswordField
              id="newPassword"
              label={t('resetPassword.newPasswordLabel')}
              value={field.value}
              onChange={field.onChange}
              showCriteria
              autoComplete="new-password"
              required
            />
          )}
        />
        <Controller
          control={form.control}
          name="confirm"
          render={({ field }) => (
            <PasswordField
              id="confirm"
              label={t('shared.confirmPasswordLabel')}
              value={field.value}
              onChange={field.onChange}
              autoComplete="new-password"
              required
            />
          )}
        />
        {errors.confirm ? (
          <p className="text-[13px] text-jq-danger">{errors.confirm.message}</p>
        ) : null}
        <Button type="submit" disabled={isSubmitting} className="w-full">
          {isSubmitting ? t('resetPassword.submitting') : t('resetPassword.submit')}
        </Button>
      </form>
    </AuthShell>
  );
}
